import { useState } from 'react';
import { InvestigadorRecord, Publicacion } from '../types';

interface Props {
  records: InvestigadorRecord[];
}

function getBaseDatosLabel(pub: Publicacion): string {
  if (!pub.baseDatos) return '';
  if (pub.baseDatos === 'Otras (especificar)') return pub.baseDatosOtra || 'Otra';
  return pub.baseDatos;
}

function PubRow({ pub, index }: { pub: Publicacion; index: number }) {
  const bdLabel = getBaseDatosLabel(pub);
  const tieneLink = !!pub.link?.trim();
  const tieneArchivo = !!pub.archivoNombre?.trim();
  const archivoUrl = pub.archivoUrl ?? '';

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <div className="w-5 h-5 flex items-center justify-center bg-[#0D3157]/10 rounded-full flex-shrink-0 mt-0.5">
        <span className="text-[#0D3157] text-xs font-bold">{index + 1}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-slate-700 leading-snug">{pub.titulo}</p>
        <div className="flex items-center gap-2 flex-wrap mt-1.5">
          {bdLabel && (
            <span className="inline-flex items-center gap-1 bg-[#0D3157]/8 text-[#0D3157] text-xs font-medium px-2 py-0.5 rounded-full">
              <i className="ri-database-2-line text-xs" />
              {bdLabel}
            </span>
          )}
          {tieneLink && (
            <a
              href={pub.link}
              target="_blank"
              rel="nofollow noopener noreferrer"
              title={pub.link}
              className="inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-medium px-2 py-0.5 rounded-full transition-colors cursor-pointer max-w-[200px] truncate"
            >
              <i className="ri-external-link-line text-xs flex-shrink-0" />
              <span className="truncate">{pub.link}</span>
            </a>
          )}
          {tieneArchivo && archivoUrl && (
            <a
              href={archivoUrl}
              target="_blank"
              rel="noopener noreferrer"
              download={pub.archivoNombre}
              title={`Descargar: ${pub.archivoNombre}`}
              className="inline-flex items-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold px-2.5 py-0.5 rounded-full transition-colors cursor-pointer border border-emerald-200 whitespace-nowrap"
            >
              <i className="ri-attachment-2 text-xs" />
              {pub.archivoNombre}
              <i className="ri-download-2-line text-xs" />
            </a>
          )}
          {tieneArchivo && !archivoUrl && (
            <span
              title={pub.archivoNombre}
              className="inline-flex items-center gap-1 bg-slate-100 text-slate-400 text-xs px-2 py-0.5 rounded-full"
            >
              <i className="ri-file-line text-xs" />
              {pub.archivoNombre}
              <i className="ri-error-warning-line text-xs text-amber-400" title="URL no disponible" />
            </span>
          )}
          {!tieneLink && !tieneArchivo && (
            <span className="text-xs text-slate-300 italic">Sin enlace ni archivo</span>
          )}
        </div>
      </div>
    </div>
  );
}

function InvestigadorRow({
  record,
  index,
}: {
  record: InvestigadorRecord;
  index: number;
}) {
  const [expanded, setExpanded] = useState(false);

  const archivosCount = record.publicaciones.filter(
    (p) => p.archivoNombre && p.archivoUrl
  ).length;

  return (
    <>
      <tr
        className={`cursor-pointer transition-colors ${
          expanded
            ? 'bg-[#0D3157]/5'
            : index % 2 === 0
            ? 'bg-white hover:bg-slate-50'
            : 'bg-slate-50/60 hover:bg-slate-100/60'
        }`}
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-4 py-3 text-slate-700 font-semibold text-xs">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center bg-[#0D3157] rounded-full flex-shrink-0">
              <span className="text-white text-xs font-bold">
                {record.nombre.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <p className="truncate font-semibold text-slate-700">
                {record.nombre} {record.apellido}
              </p>
              {record.orcid && (
                <p className="text-xs text-slate-400 truncate font-normal">{record.orcid}</p>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-slate-500 text-xs">
          <a
            href={`mailto:${record.email}`}
            onClick={(e) => e.stopPropagation()}
            className="hover:text-[#0D3157] hover:underline cursor-pointer"
          >
            {record.email}
          </a>
        </td>
        <td className="px-4 py-3">
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
              record.tipoUsuario === 'docente'
                ? 'bg-[#0D3157]/10 text-[#0D3157]'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            {record.tipoUsuario === 'docente' ? 'Docente' : 'Estudiante'}
          </span>
        </td>
        <td className="px-4 py-3 text-slate-500 text-xs max-w-[120px]">
          <p className="truncate">{record.unidad}</p>
        </td>
        <td className="px-4 py-3 text-center">
          <div className="flex items-center justify-center gap-2">
            <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-xs font-semibold px-2 py-0.5 rounded-full">
              <i className="ri-book-2-line text-xs" />
              {record.publicaciones.length}
            </span>
            {archivosCount > 0 && (
              <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-emerald-200">
                <i className="ri-attachment-2 text-xs" />
                {archivosCount}
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
          {record.fechaRegistro}
        </td>
        <td className="px-4 py-3 text-center">
          <div className="w-5 h-5 flex items-center justify-center mx-auto text-slate-400">
            <i
              className={`text-sm transition-transform duration-200 ${
                expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
              }`}
            />
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="bg-[#0D3157]/3">
          <td colSpan={7} className="px-0 py-0">
            <div className="border-t border-[#0D3157]/10 px-6 py-4">
              {/* Datos extra del investigador */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {record.cargo && (
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-briefcase-line text-slate-400 text-xs" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium">Cargo</p>
                      <p className="text-xs text-slate-600">
                        {record.cargo}
                        {record.materiaDocente ? ` — ${record.materiaDocente}` : ''}
                        {record.cargoAdminDetalle ? ` — ${record.cargoAdminDetalle}` : ''}
                      </p>
                    </div>
                  </div>
                )}
                {record.profesion && (
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-graduation-cap-line text-slate-400 text-xs" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium">Profesión</p>
                      <p className="text-xs text-slate-600">{record.profesion}</p>
                    </div>
                  </div>
                )}
                {record.telefono && (
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-phone-line text-slate-400 text-xs" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium">Teléfono</p>
                      <p className="text-xs text-slate-600">{record.telefono}</p>
                    </div>
                  </div>
                )}
                {(record.carreraEstudiante || record.nivelEstudiante) && (
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-school-line text-slate-400 text-xs" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium">Carrera / Nivel</p>
                      <p className="text-xs text-slate-600">
                        {record.carreraEstudiante}
                        {record.nivelEstudiante ? ` — ${record.nivelEstudiante}` : ''}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Publicaciones */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-file-list-3-line text-[#0D3157] text-sm" />
                  </div>
                  <span className="text-xs font-bold text-[#0D3157]">
                    Publicaciones ({record.publicaciones.length})
                  </span>
                  {archivosCount > 0 && (
                    <span className="ml-auto inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-emerald-200">
                      <i className="ri-attachment-2 text-xs" />
                      {archivosCount} archivo{archivosCount !== 1 ? 's' : ''} en Storage
                    </span>
                  )}
                </div>
                <div className="px-4 py-1 divide-y divide-slate-100">
                  {record.publicaciones.length > 0 ? (
                    record.publicaciones.map((pub, idx) => (
                      <PubRow key={pub.id} pub={pub} index={idx} />
                    ))
                  ) : (
                    <p className="text-xs text-slate-400 italic py-3 text-center">
                      Sin publicaciones asociadas
                    </p>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function RegistrosAdminTable({ records }: Props) {
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState<'todos' | 'docente' | 'estudiante'>('todos');

  const filtered = records.filter((r) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      r.nombre.toLowerCase().includes(q) ||
      r.apellido.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      r.unidad.toLowerCase().includes(q) ||
      r.profesion.toLowerCase().includes(q);
    const matchTipo = filterTipo === 'todos' || r.tipoUsuario === filterTipo;
    return matchSearch && matchTipo;
  });

  const totalArchivos = records.reduce(
    (acc, r) => acc + r.publicaciones.filter((p) => p.archivoNombre && p.archivoUrl).length,
    0
  );

  return (
    <div className="space-y-3">
      {/* Barra de filtros */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-slate-400 text-sm" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, correo, profesión…"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-xs text-slate-600 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/20 focus:border-[#0D3157] bg-white"
          />
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {(['todos', 'docente', 'estudiante'] as const).map((tipo) => (
            <button
              key={tipo}
              type="button"
              onClick={() => setFilterTipo(tipo)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                filterTipo === tipo
                  ? 'bg-white text-[#0D3157] border border-slate-200'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tipo === 'todos' ? 'Todos' : tipo === 'docente' ? 'Docentes' : 'Estudiantes'}
            </button>
          ))}
        </div>
        {totalArchivos > 0 && (
          <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1.5 rounded-lg border border-emerald-200 whitespace-nowrap">
            <i className="ri-folder-cloud-line text-sm" />
            {totalArchivos} archivo{totalArchivos !== 1 ? 's' : ''} en bucket CENSO
          </span>
        )}
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-[#0D3157] text-white">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Investigador</th>
              <th className="px-4 py-2.5 text-left font-semibold">Correo</th>
              <th className="px-4 py-2.5 text-left font-semibold">Tipo</th>
              <th className="px-4 py-2.5 text-left font-semibold">Unidad</th>
              <th className="px-4 py-2.5 text-center font-semibold">Publicaciones</th>
              <th className="px-4 py-2.5 text-left font-semibold">Fecha</th>
              <th className="px-4 py-2.5 text-center font-semibold w-10">
                <i className="ri-expand-up-down-line" />
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? (
              filtered.map((r, i) => (
                <InvestigadorRow key={r.id} record={r} index={i} />
              ))
            ) : (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                  <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-2">
                    <i className="ri-search-line text-xl text-slate-300" />
                  </div>
                  <p className="text-sm font-medium text-slate-500">No se encontraron registros</p>
                  {search && (
                    <p className="text-xs text-slate-400 mt-1">
                      Intente con otro término de búsqueda
                    </p>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > 0 && (
        <p className="text-xs text-slate-400 text-right">
          Mostrando {filtered.length} de {records.length} registros — haz clic en una fila para ver sus publicaciones
        </p>
      )}
    </div>
  );
}
